import { AttributionWindow, ProductType } from '../api'
import { ITextInput } from '@ads/front-core/components/Form/TextInput'

type ProductConfigItem = {
  withDevices: boolean
  type: ITextInput['type']
  rateKeys: ('DEFAULT' | AttributionWindow)[]
}

export type ProductConfig = {
  productType: ProductType
} & ProductConfigItem

export enum ProductFor {
  NONPUBLISHER,
  PUBLISHER,
}

export const PRODUCTS: {
  key: ProductType
  label: string
  availableFor: ProductFor[]
}[] = [
  {
    key: 'RETARGETING',
    label: 'WP Booster ROAS',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'MAILING_RETARGETING',
    label: 'Mailing retargeting',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'MAILING_PROSPECTING',
    label: 'Mailing dynamiczny',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'MAILING_STATIC',
    label: 'Mailing statyczny (do Inboxa)',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'MAILING_STATIC_OTHER',
    label: 'Mailing statyczny (do Segregatora)',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'MAILING_STATIC_CPM',
    label: 'Mailing statyczny CPM',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'PROSPECTING',
    label: 'Display dynamiczny (stary)',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'PROSPECTING_2',
    label: 'Display dynamiczny',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'FEED_CPSC',
    label: 'WP Booster CPSC (Dynamiczny)',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'DISPLAY_CPC',
    label: 'Display CPC',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'UNIVERSAL_CPC',
    label: 'Universal CPC',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'UNIVERSAL_CPM',
    label: 'Universal CPM',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'UNIVERSAL_VCPM',
    label: 'Universal VCPM',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'DISPLAY_CPM',
    label: 'Display CPM',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'DISPLAY_VCPM',
    label: 'Display vCPM',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'DISPLAY_CPSC',
    label: 'Display CPSC',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'MAILING_CRM',
    label: 'Newsletter',
    availableFor: [ProductFor.NONPUBLISHER, ProductFor.PUBLISHER],
  },
  {
    key: 'PUBLISHERS_DISPLAY_VCPM',
    label: 'Display vCPM dla wydawców',
    availableFor: [ProductFor.PUBLISHER],
  },
  {
    key: 'PUBLISHERS_DISPLAY_CPM',
    label: 'Display CPM dla wydawców',
    availableFor: [ProductFor.PUBLISHER],
  },
  {
    key: 'PUBLISHERS_DISPLAY_CPC',
    label: 'Display CPC dla wydawców',
    availableFor: [ProductFor.PUBLISHER],
  },
  {
    key: 'PUBLISHERS_FLAT_FEE',
    label: 'Flatfee dla wydawców',
    availableFor: [ProductFor.PUBLISHER],
  },
  {
    key: 'PUBLISHERS_SCREENING',
    label: 'Screening dla wydawców',
    availableFor: [ProductFor.PUBLISHER],
  },
  {
    key: 'PUSH',
    label: 'Push',
    availableFor: [ProductFor.NONPUBLISHER, ProductFor.PUBLISHER],
  },
  {
    key: 'NATIVE_LINK_DIRECT',
    label: 'Native Link Direct',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'NATIVE_SG',
    label: 'Reklama natywna na SG WP',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'START_AD',
    label: 'Start Ad',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'PAUSE_AD',
    label: 'Pause Ad',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'OUTSTREAM_VIDEO_VCPM',
    label: 'Video outstream in-banner (vCPM)',
    availableFor: [ProductFor.NONPUBLISHER],
  },
  {
    key: 'OUTSTREAM_VIDEO_CPM',
    label: 'Video outstream in-banner (CPM)',
    availableFor: [ProductFor.NONPUBLISHER],
  },
]

export const PRODUCTS_MIN_RATES: Partial<
  Record<ProductType, ProductConfigItem>
> = {
  UNIVERSAL_CPC: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  UNIVERSAL_CPM: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  UNIVERSAL_VCPM: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  DISPLAY_CPC: {
    withDevices: true,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  DISPLAY_CPC_NATIVE: {
    withDevices: true,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  DISPLAY_CPM: {
    withDevices: true,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  DISPLAY_VCPM: {
    withDevices: true,
    type: 'number',
    rateKeys: ['DEFAULT'],
  },
  DISPLAY_CPSC: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  FEED_CPSC: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  PROSPECTING: {
    withDevices: true,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  PROSPECTING_2: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  MAILING_PROSPECTING: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  MAILING_RETARGETING: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  MAILING_STATIC: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  MAILING_STATIC_OTHER: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  MAILING_STATIC_CPM: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  NATIVE_LINK_DIRECT: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  NATIVE_SG: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  START_AD: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  PAUSE_AD: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  PUBLISHERS_DISPLAY_CPC: {
    withDevices: true,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  PUBLISHERS_DISPLAY_CPM: {
    withDevices: true,
    type: 'number',
    rateKeys: ['DEFAULT'],
  },
  PUBLISHERS_DISPLAY_VCPM: {
    withDevices: true,
    type: 'number',
    rateKeys: ['DEFAULT'],
  },
  RETARGETING: {
    withDevices: false,
    type: 'number',
    rateKeys: ['LAST_CLICK_1D', 'LAST_CLICK', 'D2', 'D7', 'D14', 'D30'],
  },
  OUTSTREAM_VIDEO_CPM: {
    withDevices: false,
    type: 'currency',
    rateKeys: ['DEFAULT'],
  },
  OUTSTREAM_VIDEO_VCPM: {
    withDevices: false,
    type: 'number',
    rateKeys: ['DEFAULT'],
  },
}
